<template>
  <div>
    <header-component />
    <div class="px-2 py-2 w-full">
      <div class="card">
        <div class="flex justify-between items-center p-2">
          <h3 class="mb-0 w-full font-bold text-gray-800 text-md" style="text-transform: capitalize">
            Branches
          </h3>
          <base-button size="sm" type="primary" @click="openAdd">Add Branch</base-button>
        </div>

        <div v-if="loading" class="flex justify-center py-12">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Name</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Legal Name</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Address</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Phone</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">GST</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Color</th>
                <th class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="branch in branches" :key="branch.id" class="hover:bg-gray-50">
                <td class="px-2 py-2 text-sm text-gray-900 border-b font-medium">
                  <span
                    class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                    :style="branch.color ? { background: branch.color, color: '#fff' } : { background: '#dbeafe', color: '#1e40af' }"
                  >
                    {{ branch.name }}
                  </span>
                </td>
                <td class="px-2 py-2 text-sm text-gray-500 border-b">{{ branch.legal_name || '—' }}</td>
                <td class="px-2 py-2 text-sm text-gray-900 border-b">{{ branch.address }}</td>
                <td class="px-2 py-2 text-sm text-gray-900 whitespace-nowrap border-b">{{ branch.phone }}</td>
                <td class="px-2 py-2 text-sm border-b">
                  <span v-if="branch.is_gst" class="text-green-700 font-medium text-xs">
                    GST: {{ branch.gst_number || '—' }}
                  </span>
                  <span v-else class="text-gray-400 text-xs">Non-GST</span>
                </td>
                <td class="px-2 py-2 border-b">
                  <span
                    v-if="branch.color"
                    class="inline-block w-6 h-6 rounded-full border border-gray-200"
                    :style="{ background: branch.color }"
                    :title="branch.color"
                  ></span>
                  <span v-else class="text-gray-400 text-xs">—</span>
                </td>
                <td class="px-2 py-2 text-sm whitespace-nowrap border-b">
                  <div class="flex items-center gap-2">
                    <base-button size="sm" type="info" @click="viewLedger(branch)">Ledger</base-button>
                    <base-button size="sm" type="secondary" @click="openEdit(branch)">Edit</base-button>
                    <base-button size="sm" type="danger" @click="deleteBranch(branch)">Delete</base-button>
                  </div>
                </td>
              </tr>
              <tr v-if="!branches || branches.length === 0">
                <td colspan="7" class="px-2 py-4 text-sm text-center text-gray-500 border-b">No branches found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
    >
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 overflow-y-auto" style="max-height: 90vh">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold text-gray-800">{{ editing ? 'Edit Branch' : 'Add Branch' }}</h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600">&#x2715;</button>
        </div>

        <!-- Name -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Name <span class="text-red-500">*</span></label>
          <input v-model="form.name" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" :class="{ 'border-red-400': formErrors.name }" placeholder="Branch name" />
          <p v-if="formErrors.name" class="text-red-500 text-xs mt-1">{{ formErrors.name }}</p>
        </div>

        <!-- Legal Name -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Legal Name</label>
          <input v-model="form.legal_name" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="As on GST certificate / invoice" />
        </div>

        <!-- Address -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Address <span class="text-red-500">*</span></label>
          <textarea v-model="form.address" rows="2" class="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500" :class="{ 'border-red-400': formErrors.address }" placeholder="Branch address"></textarea>
          <p v-if="formErrors.address" class="text-red-500 text-xs mt-1">{{ formErrors.address }}</p>
        </div>

        <!-- Phone -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Phone <span class="text-red-500">*</span></label>
          <input v-model="form.phone" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" :class="{ 'border-red-400': formErrors.phone }" placeholder="Phone number" />
          <p v-if="formErrors.phone" class="text-red-500 text-xs mt-1">{{ formErrors.phone }}</p>
        </div>

        <!-- GST toggle -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">GST Registration</label>
          <div class="flex gap-4">
            <label class="flex items-center gap-2 cursor-pointer text-sm">
              <input type="radio" :value="false" v-model="form.is_gst" class="accent-blue-600" />
              Non-GST
            </label>
            <label class="flex items-center gap-2 cursor-pointer text-sm">
              <input type="radio" :value="true" v-model="form.is_gst" class="accent-blue-600" />
              GST Registered
            </label>
          </div>
        </div>

        <!-- GST Number (conditional) -->
        <div v-if="form.is_gst" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
          <input v-model="form.gst_number" type="text" maxlength="20" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase" placeholder="e.g. 27AAAAA0000A1Z5" />
        </div>

        <!-- Color -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Badge Color</label>
          <div class="flex items-center gap-3">
            <input v-model="form.color" type="color" class="h-9 w-16 rounded border border-gray-300 cursor-pointer p-0.5" />
            <input v-model="form.color" type="text" maxlength="20" class="w-32 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="#3b82f6" />
            <span
              class="inline-flex px-3 py-1 text-xs font-semibold rounded-full"
              :style="form.color ? { background: form.color, color: '#fff' } : { background: '#dbeafe', color: '#1e40af' }"
            >
              {{ form.name || 'Preview' }}
            </span>
          </div>
          <p class="text-gray-400 text-xs mt-1">Used for branch badges across the app.</p>
        </div>

        <!-- Latitude -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
          <input v-model="form.latitude" type="number" step="0.000001" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="e.g. 18.520430" />
          <p class="text-gray-400 text-xs mt-1">Used for geofencing attendance within 100m radius</p>
        </div>

        <!-- Longitude -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
          <input v-model="form.longitude" type="number" step="0.000001" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="e.g. 73.856743" />
          <p class="text-gray-400 text-xs mt-1">Used for geofencing attendance within 100m radius</p>
        </div>

        <div class="flex gap-3 mt-6">
          <base-button type="secondary" @click="closeModal" class="flex-1">Cancel</base-button>
          <base-button type="primary" @click="save" :disabled="saving" class="flex-1">
            <span v-if="saving"><span class="inline-block animate-spin mr-1">&#8987;</span> Saving...</span>
            <span v-else>Save</span>
          </base-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";
import headerComponent from "../Components/HeaderComponent";

const emptyForm = () => ({
  name: "",
  legal_name: "",
  address: "",
  phone: "",
  is_gst: false,
  gst_number: "",
  color: "",
  latitude: "",
  longitude: ""
});

export default {
  name: "BranchList",
  components: { "header-component": headerComponent },
  data() {
    return {
      branches: [],
      loading: false,
      showModal: false,
      editing: null,
      form: emptyForm(),
      formErrors: {},
      saving: false
    };
  },
  created() {
    this.fetchBranches();
  },
  methods: {
    async fetchBranches() {
      this.loading = true;
      try {
        const res = await new Api().call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`);
        this.branches = (res && res.data && res.data.data) || [];
      } catch {
        window.notify && window.notify({ message: "Failed to load branches.", type: "danger", title: "Error" });
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.editing = null;
      this.form = emptyForm();
      this.formErrors = {};
      this.showModal = true;
    },
    openEdit(branch) {
      this.editing = branch;
      this.form = {
        name: branch.name || "",
        legal_name: branch.legal_name || "",
        address: branch.address || "",
        phone: branch.phone || "",
        is_gst: !!branch.is_gst,
        gst_number: branch.gst_number || "",
        color: branch.color || "",
        latitude: branch.latitude != null ? branch.latitude : "",
        longitude: branch.longitude != null ? branch.longitude : ""
      };
      this.formErrors = {};
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editing = null;
      this.form = emptyForm();
      this.formErrors = {};
    },
    validate() {
      const errors = {};
      if (!this.form.name || !this.form.name.trim()) errors.name = "Name is required.";
      if (!this.form.address || !this.form.address.trim()) errors.address = "Address is required.";
      if (!this.form.phone || !this.form.phone.trim()) errors.phone = "Phone is required.";
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },
    async save() {
      if (!this.validate()) return;
      this.saving = true;
      const payload = {
        name: this.form.name.trim(),
        legal_name: this.form.legal_name ? this.form.legal_name.trim() : null,
        address: this.form.address.trim(),
        phone: this.form.phone.trim(),
        is_gst: this.form.is_gst,
        gst_number: this.form.is_gst && this.form.gst_number ? this.form.gst_number.trim().toUpperCase() : null,
        color: this.form.color ? this.form.color.trim() : null
      };
      if (this.form.latitude !== "" && this.form.latitude !== null) payload.latitude = this.form.latitude;
      if (this.form.longitude !== "" && this.form.longitude !== null) payload.longitude = this.form.longitude;

      try {
        if (this.editing) {
          await new Api().call("patch", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches/${this.editing.id}`, payload);
          window.notify && window.notify({ message: "Branch updated.", type: "success", title: "Success" });
        } else {
          await new Api().call("post", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`, payload);
          window.notify && window.notify({ message: "Branch created.", type: "success", title: "Success" });
        }
        this.closeModal();
        await this.fetchBranches();
      } catch {
        window.notify && window.notify({ message: "Failed to save. Please try again.", type: "danger", title: "Error" });
      } finally {
        this.saving = false;
      }
    },
    viewLedger(branch) {
      this.$router.push("/branches/" + branch.id + "/ledger");
    },
    async deleteBranch(branch) {
      if (!window.confirm(`Delete branch "${branch.name}"?`)) return;
      try {
        await new Api().call("delete", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches/${branch.id}`);
        window.notify && window.notify({ message: "Branch deleted.", type: "success", title: "Success" });
        await this.fetchBranches();
      } catch {
        window.notify && window.notify({ message: "Failed to delete.", type: "danger", title: "Error" });
      }
    }
  }
};
</script>
